/**
 * ws_client.js (native WebSocket version)
 */

export const WS_STATE = {
  DISCONNECTED: "disconnected",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  ERROR: "error",
};

export class WSClient {
  constructor(url) {
    this.url = url;
    this.ws = null;
    this.state = WS_STATE.DISCONNECTED;

    // callbacks
    this._onFastResult = null;
    this._onRefineResult = null;
    this._onStatus = null;
    this._onStateChange = null;
    this._onAudioAck = null;
    this._acceptedSamples = 0;
    this._sessionId = null;
    this._ingressEpoch = null;
  }

  /* =========================
   * State
   * ========================= */
  _setState(state, context = {}) {
    if (this.state === state) return;
    this.state = state;
    console.log("[ws] state:", state);
    this._onStateChange?.(state, context);
  }

  isConnected() {
    return this.state === WS_STATE.CONNECTED;
  }

  getSocketIdentity() {
    return this.isConnected() && this.ws && this.ws.readyState === 1 ? this.ws : null;
  }

  /** current ingress receipt 的只讀快照；Mic 換裝置時用來建立 ACK 收樣邊界。 */
  getAudioAcceptanceState() {
    return {
      sessionId: this._sessionId,
      ingressEpoch: this._ingressEpoch,
      acceptedSamples: this._acceptedSamples,
    };
  }

  /* =========================
   * Connection
   * ========================= */
  connect(sessionId = null) {
    if (this.ws || this.isConnected()) return;
    this._acceptedSamples = 0;
    this._sessionId = sessionId == null ? null : String(sessionId);
    this._ingressEpoch = null;

    const url = sessionId
      ? `${this.url}${this.url.includes("?") ? "&" : "?"}session_id=${encodeURIComponent(sessionId)}`
      : this.url;
    console.log("[ws] connecting:", url);
    this._setState(WS_STATE.CONNECTING);

    const socket = new WebSocket(url);
    this.ws = socket;
    socket.binaryType = "arraybuffer";

    socket.onopen = () => {
      // close() 後瀏覽器仍可能回報舊握手成功；舊 socket 不得把後開 transport
      // 的共用 state 改回 connected，更不能接手後續音訊。
      if (this.ws !== socket) {
        socket.close();
        return;
      }
      this._setState(WS_STATE.CONNECTED);
      this._emitStatus("connected");
    };

    socket.onclose = () => {
      if (this.ws !== socket) return;
      console.warn("[ws] closed by server");
      this.ws = null;
      this._sessionId = null;
      this._ingressEpoch = null;
      this._setState(WS_STATE.DISCONNECTED);
      this._emitStatus("disconnected");
    };

    socket.onerror = (err) => {
      if (this.ws !== socket) return;
      console.error("[ws] error:", err);
      this._setState(WS_STATE.ERROR);
      this._emitStatus("error");
    };

    socket.onmessage = (evt) => {
      if (this.ws !== socket) return;
      this._handleMessage(evt.data);
    };
  }

  disconnect() {
    if (!this.ws) return;
    console.log("[ws] disconnect");
    const socket = this.ws;
    this.ws = null;
    this._sessionId = null;
    this._ingressEpoch = null;
    socket.close();
    // 不等非同步 close callback：下一場連線必須立即取得乾淨 identity；舊 callback
    // 已由上方 identity guard 隔離。
    this._setState(WS_STATE.DISCONNECTED, { intentional: true });
    this._emitStatus("disconnected");
  }

  /* =========================
   * Sending
   * ========================= */
  sendAudioChunk(chunk, metadata = null) {
    if (!this.isConnected() || !this.ws || this.ws.readyState !== 1) return false;
    if (!(chunk instanceof Float32Array)) return false;

    // send raw PCM float32
    // console.log("[ws] sendAudioChunk");
    try {
      if (metadata) {
        this.ws.send(JSON.stringify({
          type: "audio_chunk",
          start_sample: Number(metadata.startSample),
          end_sample: Number(metadata.endSample),
        }));
      }
      this.ws.send(chunk.slice().buffer);
      return true;
    } catch (err) {
      console.error("[ws] sendAudioChunk failed", err);
      return false;
    }
  }

  async waitForAcceptedSamples(targetSamples, {
    timeoutMs = 5000,
    pollMs = 20,
    isCancelled = null,
  } = {}) {
    const target = Number(targetSamples);
    if (!Number.isSafeInteger(target) || target < 0) return false;
    const deadline = Date.now() + Math.max(0, Number(timeoutMs) || 0);
    const socket = this.getSocketIdentity();
    if (!socket) return false;
    while (true) {
      if (typeof isCancelled === "function" && isCancelled()) return false;
      if (!this.isConnected() || this.ws !== socket || socket.readyState !== 1) return false;
      if (this._acceptedSamples >= target) return true;
      if (Date.now() >= deadline) return false;
      await new Promise((resolve) => setTimeout(resolve, Math.max(1, Number(pollMs) || 20)));
    }
  }

  /**
   * 瀏覽器 WebSocket 沒有逐包 ACK；EOS 前可用的最強保證是 socket 仍 OPEN，
   * 且本地 send queue 已排空。輪詢有 deadline，也接受 lifecycle cancel，避免
   * 大檔尾段同步灌入記憶體或斷線後仍送 EOS。
   */
  async waitForBufferedAmount(maxBufferedAmount = 0, {
    timeoutMs = 5000,
    pollMs = 20,
    isCancelled = null,
  } = {}) {
    const deadline = Date.now() + Math.max(0, Number(timeoutMs) || 0);
    const threshold = Math.max(0, Number(maxBufferedAmount) || 0);
    const interval = Math.max(1, Number(pollMs) || 20);
    const socket = this.getSocketIdentity();
    if (!socket) return false;

    while (true) {
      if (typeof isCancelled === "function" && isCancelled()) return false;
      if (!this.isConnected() || this.ws !== socket || socket.readyState !== 1) return false;
      if (Date.now() >= deadline) return false;
      if ((Number(socket.bufferedAmount) || 0) <= threshold) return true;
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }

  sendJson(obj) {
    if (!this.isConnected()) return false;
    try {
      this.ws.send(JSON.stringify(obj));
      return true;
    } catch (err) {
      console.error("[ws] sendJson failed", err);
      return false;
    }
  }

  /** PCM 後的同流序列 marker；HTTP EOS 會等 server 接受這個 watermark。 */
  sendAudioEnd({ sessionId, targetSamples, sampleRate }) {
    return this.sendJson({
      type: "audio_end",
      session_id: sessionId,
      target_samples: targetSamples,
      sample_rate: sampleRate,
    });
  }

  /* =========================
   * Receiving
   * ========================= */
  _handleMessage(data) {
    if (typeof data === "string") {
      try {
        const msg = JSON.parse(data);
        this._handleJsonMessage(msg);
      } catch {
        console.warn("[ws] invalid json:", data);
      }
    }
  }

  _handleJsonMessage(msg) {
    const { type, payload } = msg;

    switch (type) {
      case "fast_result":
        this._onFastResult?.(payload);
        break;
      case "refine_result":
        this._onRefineResult?.(payload);
        break;
      case "status":
        this._emitStatus(payload);
        break;
      case "audio_receipt": {
        const epoch = Number(payload && payload.ingress_epoch);
        if (
          payload
          && String(payload.session_id || '') === String(this._sessionId || '')
          && Number.isSafeInteger(epoch)
          && epoch > 0
          && Number.isSafeInteger(Number(payload.accepted_samples))
        ) {
          this._ingressEpoch = epoch;
          this._acceptedSamples = Number(payload.accepted_samples);
          this._onAudioAck?.(payload, type);
        }
        break;
      }
      case "audio_ack":
        if (
          payload
          && String(payload.session_id || '') === String(this._sessionId || '')
          && Number(payload.ingress_epoch) === this._ingressEpoch
          && Number.isSafeInteger(Number(payload.accepted_samples))
        ) {
          this._acceptedSamples = Number(payload.accepted_samples);
          this._onAudioAck?.(payload, type);
        }
        break;
      case "audio_end_ack":
        if (payload && String(payload.session_id || '') === String(this._sessionId || '')) {
          this._onAudioAck?.(payload, type);
        }
        break;
      default:
        console.warn("[ws] unknown message:", msg);
    }
  }

  /* =========================
   * Callbacks
   * ========================= */
  onFastResult(cb) {
    this._onFastResult = cb;
  }

  onRefineResult(cb) {
    this._onRefineResult = cb;
  }

  onStatus(cb) {
    this._onStatus = cb;
  }

  onStateChange(cb) {
    this._onStateChange = cb;
  }

  onAudioAck(cb) {
    this._onAudioAck = cb;
  }

  _emitStatus(status) {
    this._onStatus?.(status);
  }
}
